import axios from 'axios';

import ICoreApiProvider from './ICoreApiProvider';
import IGbContext from '../../interfaces/IGbContext';
import ICoreRoom from '../../interfaces/ICoreRoom';
import { ConfigurationState } from '../../store/configurationState/interfaces';
import handleApiError from '../bimVisualizerApi/errorHandler';
import { normalizeGuid } from '../../utils/guidUtils';
import { RoomNoteSaveResult, roomNoteSaveErrorFromApiError } from './RoomNoteSaveResult';
import { RoomNoteSaveErrorEnum } from '../../enums/RoomNoteSaveErrorEnum';
import { coreApartmentRoomsSchema } from './schema/coreApartmentRoomsSchema';
import { coreUpdateRoomsResponseSchema } from './schema/coreUpdateRoomsResponseSchema';
import { coreUserRightsCheckSchema } from './schema/coreUserRightsCheckSchema';
import { coreApartmentBundlesSchema } from './schema/coreApartmentBundlesSchema';
import { coreTenantSettingsSchema } from './schema/coreTenantSettingsSchema';
import ICoreTenantSettings from '../../interfaces/ICoreTenantSettings';
import { ICoreBundleMaterial } from '../../interfaces/ICoreBundle';
import ICoreApartmentBundles, { ICoreBundleElement } from '../../interfaces/ICoreApartmentBundles';
import { coreDetachRoomElementsResponseSchema } from './schema/coreDetachRoomElementsSchema';
import { BundleDetachResult, DetachRoomElementsBody, bundleDetachErrorFromApiError } from './BundleDetachResult';
import { BundleDetachErrorEnum } from '../../enums/BundleDetachErrorEnum';

// Treat a null, empty or whitespace-only comment as no note
const normalizeComment = (comment: string | null | undefined): string | null =>
    comment?.trim() ? comment.trim() : null;

// CoreAPI sends quantities as decimal strings
const parseAmount = (amount: string | number | null | undefined): number | null => {
    if (amount === null || amount === undefined || amount === "") return null;
    const value = Number(amount);
    return Number.isFinite(value) ? value : null;
};

// Empty strings mean "not set" in CoreAPI
const nonEmpty = (value: string | null | undefined): string | null => value?.trim() ? value.trim() : null;

const toMaterial = (m: { name?: string | null; manufacturer?: string | null; imageUrlSmall?: string | null } | null | undefined): ICoreBundleMaterial | null =>
    m ? { name: nonEmpty(m.name), manufacturer: nonEmpty(m.manufacturer), imageUrl: nonEmpty(m.imageUrlSmall) } : null;

const jwtSubject = (token: string | undefined): string | null => {
    try {
        const payload = token?.split('.')[1];
        if (!payload) return null;
        const json = JSON.parse(atob(payload.replace(/-/g, '+').replace(/_/g, '/')));
        const subject = json.sub ?? json.user_id;
        return subject !== undefined && subject !== null && subject !== '' ? String(subject) : null;
    } catch {
        return null;
    }
};

export default class CoreApiProvider implements ICoreApiProvider {
    configuration: ConfigurationState;
    token?: string;

    constructor(configuration: ConfigurationState, token?: string) {
        this.configuration = configuration;
        this.token = token;
    }

    // Null when CoreAPI's url isn't configured or there is no token, so callers can skip the request
    private getTenantUrl(gbContext: IGbContext): string | null {
        const coreApiUrl = this.configuration.coreApiUrl;
        if (!this.token || !coreApiUrl) {
            // Without this, room notes and the right to edit them silently disappear
            console.warn(`CoreAPI request skipped: ${!coreApiUrl ? 'VITE_CORE_API_URL is not configured' : 'no token'}`);
            return null;
        }

        const baseUrl = coreApiUrl.href.endsWith('/') ? coreApiUrl.href : `${coreApiUrl.href}/`;
        return `${baseUrl}api/v6/private/organisations/${gbContext.organisationId}/tenants/${gbContext.tenantId}`;
    }

    private getProjectUrl(gbContext: IGbContext): string | null {
        const tenantUrl = this.getTenantUrl(gbContext);
        return tenantUrl ? `${tenantUrl}/projects/${gbContext.projectId}` : null;
    }

    isAvailable = (): boolean => !!this.token && !!this.configuration.coreApiUrl;

    private getHeaders() {
        return {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${this.token}`
        };
    }

    getApartmentRooms = async (gbContext: IGbContext): Promise<ICoreRoom[] | null> => {
        const projectUrl = this.getProjectUrl(gbContext);
        if (!projectUrl || !gbContext.apartmentId) return null;

        try {
            const response = await axios.get(`${projectUrl}/apartments/${gbContext.apartmentId}`, {
                headers: this.getHeaders(),
                params: { rooms: true }
            });

            const rooms = coreApartmentRoomsSchema.parse(response.data).data.rooms ?? [];

            // Rooms without an ifcguid can never match a space
            return rooms.flatMap(room => {
                const globalId = normalizeGuid(room.ifcguid);
                return globalId ? [{ id: room.id, globalId, note: normalizeComment(room.comment) }] : [];
            });
        } catch (error) {
            if (!handleApiError(error)) {
                console.error('Error fetching apartment rooms:', error);
            }
        }
        return null;
    };

    // CoreAPI checks no right and doesn't check that the room belongs to the project, so only pass ids from getApartmentRooms
    updateRoomComment = async (gbContext: IGbContext, roomId: number, comment: string | null): Promise<RoomNoteSaveResult> => {
        const projectUrl = this.getProjectUrl(gbContext);
        if (!projectUrl) return { ok: false, error: RoomNoteSaveErrorEnum.Unauthorized };

        const normalized = normalizeComment(comment);

        try {
            const response = await axios.put(
                `${projectUrl}/rooms/update`,
                { entityIds: [roomId], properties: { comment: normalized } },
                { headers: this.getHeaders() });

            const parsed = coreUpdateRoomsResponseSchema.parse(response.data);
            if (parsed.error) return { ok: false, error: RoomNoteSaveErrorEnum.Failed };
            if (parsed.data === 0) return { ok: false, error: RoomNoteSaveErrorEnum.NotFound };

            return { ok: true, notes: normalized };
        } catch (error) {
            if (!handleApiError(error)) {
                console.error('Error updating room comment:', error);
            }
            return { ok: false, error: roomNoteSaveErrorFromApiError(error) };
        }
    };

    // user-rights/check answers for each requested right, so the client needs no Shiro matcher.
    // Null when the check couldn't be made, so callers can tell "denied" from "unknown".
    checkUserRights = async (gbContext: IGbContext, rights: string[]): Promise<Record<string, boolean> | null> => {
        const projectUrl = this.getProjectUrl(gbContext);
        if (!projectUrl) return null;

        try {
            // axios sends an array as rights[]=a&rights[]=b
            const response = await axios.get(`${projectUrl}/user-rights/check`, {
                headers: this.getHeaders(),
                params: { rights }
            });

            const userRights = coreUserRightsCheckSchema.parse(response.data).data.userRights;
            return Object.fromEntries(rights.map((right) => [right, userRights[right] === true]));
        } catch (error) {
            if (!handleApiError(error)) {
                console.error('Error checking user rights:', error);
            }
        }
        return null;
    };

    // The token's subject, unverified: only used to keep one user's cached data apart from another's
    getUserId = (): string | null => jwtSubject(this.token);

    // Elements only come back nested in rooms, so rooms=true is needed
    getApartmentBundles = async (gbContext: IGbContext): Promise<ICoreApartmentBundles | null> => {
        const projectUrl = this.getProjectUrl(gbContext);
        if (!projectUrl || !gbContext.apartmentId) return null;

        try {
            const response = await axios.get(`${projectUrl}/apartments/${gbContext.apartmentId}`, {
                headers: this.getHeaders(),
                params: { bundles: true, rooms: true, elements: true, owners: false }
            });

            const data = coreApartmentBundlesSchema.parse(response.data).data;

            // Bundles hidden from the user are left out, so their elements look unbundled
            const bundles = (data.bundles ?? [])
                .filter(b => b.userCanSeeBundle !== false)
                .map(b => ({
                    id: b.id,
                    name: b.name ?? "",
                    amount: parseAmount(b.amount),
                    amountUnit: nonEmpty(b.amountUnit),
                    description: nonEmpty(b.description),
                    status: nonEmpty(b.status),
                    state: nonEmpty(b.state),
                    cost: b.hasPrice ? parseAmount(b.cost) : null,
                    selectedMaterial: toMaterial(b.selectedMaterial),
                    // CoreAPI treats a bundle in several rooms as apartment level, which isApartmentLevel says outright
                    roomId: !b.isApartmentLevel && b.roomIds?.length === 1 ? b.roomIds[0] : null
                }));

            const elements = (data.rooms ?? []).flatMap(room => (room.elements ?? []).flatMap((e): ICoreBundleElement[] => {
                const globalId = normalizeGuid(e.globalId);
                return globalId && e.bundleId != null
                    ? [{ globalId, roomElementId: e.id, bundleId: e.bundleId, name: nonEmpty(e.name), roomId: room.id, roomName: nonEmpty(room.name) }]
                    : [];
            }));

            const rooms = (data.rooms ?? []).map(room => ({ id: room.id, name: nonEmpty(room.name) }));

            return { bundles, elements, rooms };
        } catch (error) {
            if (!handleApiError(error)) {
                console.error('Error fetching apartment bundles:', error);
            }
        }
        return null;
    };

    // Moves the room elements into a copy of their bundle. CoreAPI checks bundles:manage.
    detachRoomElements = async (gbContext: IGbContext, body: DetachRoomElementsBody): Promise<BundleDetachResult> => {
        const projectUrl = this.getProjectUrl(gbContext);
        if (!projectUrl) return { ok: false, error: BundleDetachErrorEnum.Failed };

        try {
            const response = await axios.post(`${projectUrl}/bundles/room_elements/detach`, body, { headers: this.getHeaders() });
            const { newBundleId } = coreDetachRoomElementsResponseSchema.parse(response.data).data;
            return { ok: true, newBundleId };
        } catch (error) {
            if (!handleApiError(error)) {
                console.error('Error detaching room elements:', error);
            }
            return { ok: false, error: bundleDetachErrorFromApiError(error, body.targetRoomId !== null) };
        }
    };

    getTenantSettings = async (gbContext: IGbContext): Promise<ICoreTenantSettings | null> => {
        const tenantUrl = this.getTenantUrl(gbContext);
        if (!tenantUrl) return null;

        try {
            const response = await axios.get(`${tenantUrl}/settings`, { headers: this.getHeaders() });
            const data = coreTenantSettingsSchema.parse(response.data).data;
            return { currencyCode: nonEmpty(data.currencyCode) };
        } catch (error) {
            if (!handleApiError(error)) {
                console.error('Error fetching tenant settings:', error);
            }
        }
        return null;
    };
}
